/**
 * Variable profiling (brief DET-01).
 *
 * Pure summaries of an io DataTable. Missing means a null cell or an
 * empty string (the same convention the io converters use); declared
 * user-missing codes were already normalized to null by the readers
 * (brief ING-06), so the missing share includes them. Sentinel-
 * looking numbers are reported as suspected missing ONLY when a
 * value label declares missingness — never on shape alone.
 */
import { columnIndex } from '@sae/io';
/** Missing-label vocabulary (brief ING-06: labels or dictionary text). */
/** Value-label text that marks a code as a non-answer (ING-06). */
export const MISSING_LABEL_RE = /missing|refus|don'?t know|do not know|not applicable|n\/a|no answer|skipped|not asked/i;
export function isMissingCell(v) {
    return v === null || (typeof v === 'string' && v.trim() === '');
}
/** Deterministic value ordering: numbers ascending, then strings. */
export function compareValues(a, b) {
    if (typeof a === 'number' && typeof b === 'number')
        return a - b;
    if (typeof a === 'number')
        return -1;
    if (typeof b === 'number')
        return 1;
    return a < b ? -1 : a > b ? 1 : 0;
}
export function meanOf(values) {
    if (values.length === 0)
        return 0;
    return values.reduce((a, v) => a + v, 0) / values.length;
}
/** Sample standard deviation (n - 1); 0 for fewer than two values. */
export function sdOf(values) {
    if (values.length < 2)
        return 0;
    const m = meanOf(values);
    let ss = 0;
    for (const v of values)
        ss += (v - m) * (v - m);
    return Math.sqrt(ss / (values.length - 1));
}
export function medianOf(values) {
    if (values.length === 0)
        return 0;
    const sorted = [...values].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 === 1
        ? sorted[mid]
        : (sorted[mid - 1] + sorted[mid]) / 2;
}
export function pearson(xs, ys) {
    const n = Math.min(xs.length, ys.length);
    if (n < 3)
        return 0;
    const mx = meanOf(xs.slice(0, n));
    const my = meanOf(ys.slice(0, n));
    let sxy = 0;
    let sxx = 0;
    let syy = 0;
    for (let i = 0; i < n; i++) {
        const dx = xs[i] - mx;
        const dy = ys[i] - my;
        sxy += dx * dy;
        sxx += dx * dx;
        syy += dy * dy;
    }
    if (sxx <= 0 || syy <= 0)
        return 0;
    return sxy / Math.sqrt(sxx * syy);
}
function scanColumn(table, colIdx) {
    const values = new Map();
    const numericValues = [];
    let missing = 0;
    for (const row of table.rows) {
        const v = row[colIdx] ?? null;
        if (isMissingCell(v)) {
            missing++;
            continue;
        }
        const key = v;
        values.set(key, (values.get(key) ?? 0) + 1);
        if (typeof v === 'number')
            numericValues.push(v);
    }
    return { values, numericValues, missing };
}
function sortedValueCounts(data, labels) {
    const out = [];
    for (const [value, count] of data.values) {
        const label = labels.get(value);
        out.push({ value, ...(label !== undefined ? { label } : {}), count });
    }
    out.sort((a, b) => b.count - a.count || compareValues(a.value, b.value));
    return out;
}
/**
 * Profile every column. When `areaColumn` names a column, also
 * compute the within-area statistics of DET-01 (which need a
 * provisional area identifier).
 */
export function profileTable(table, areaColumn) {
    const rowCount = table.rows.length;
    const areaIdx = areaColumn !== undefined ? columnIndex(table, areaColumn) : -1;
    const columns = table.columns.map((col, colIdx) => {
        const data = scanColumn(table, colIdx);
        const labels = new Map();
        for (const vl of col.valueLabels)
            labels.set(vl.value, vl.label);
        const allCounts = sortedValueCounts(data, labels);
        const distinctCount = data.values.size;
        const nonMissing = rowCount - data.missing;
        let numeric = null;
        if (col.type === 'number' && data.numericValues.length > 0) {
            const vals = data.numericValues;
            numeric = {
                min: Math.min(...vals),
                max: Math.max(...vals),
                mean: meanOf(vals),
                sd: sdOf(vals),
                integerOnly: vals.every((v) => Number.isInteger(v)),
            };
        }
        let valueLabelCoverage = null;
        if (col.valueLabels.length > 0) {
            let labeled = 0;
            for (const value of data.values.keys()) {
                if (labels.has(value))
                    labeled++;
            }
            valueLabelCoverage = distinctCount > 0 ? labeled / distinctCount : 0;
        }
        const suspectedMissing = allCounts.filter((vc) => vc.label !== undefined && MISSING_LABEL_RE.test(vc.label));
        let withinArea = null;
        if (areaIdx >= 0 && colIdx !== areaIdx) {
            withinArea = withinAreaStats(table, colIdx, areaIdx);
        }
        return {
            name: col.name,
            label: col.label,
            type: col.type,
            storageType: col.storageType,
            rowCount,
            nonMissing,
            missingCount: data.missing,
            missingShare: rowCount > 0 ? data.missing / rowCount : 0,
            distinctCount,
            uniqueness: rowCount > 0 ? distinctCount / rowCount : 0,
            numeric,
            valueLabelCoverage,
            topValues: allCounts.slice(0, 5),
            suspectedMissing,
            withinArea,
        };
    });
    return { rowCount, columnCount: table.columns.length, columns };
}
function withinAreaStats(table, colIdx, areaIdx) {
    const byArea = new Map();
    for (const row of table.rows) {
        const a = row[areaIdx] ?? null;
        if (isMissingCell(a))
            continue;
        const key = a;
        let arr = byArea.get(key);
        if (!arr) {
            arr = [];
            byArea.set(key, arr);
        }
        const v = row[colIdx] ?? null;
        if (!isMissingCell(v))
            arr.push(v);
    }
    let constant = true;
    const withinVariances = [];
    const areaMeans = [];
    for (const vals of byArea.values()) {
        const distinct = new Set(vals);
        if (distinct.size > 1)
            constant = false;
        if (vals.length >= 2 && vals.every((v) => typeof v === 'number')) {
            const nums = vals;
            const m = meanOf(nums);
            let ss = 0;
            for (const v of nums)
                ss += (v - m) * (v - m);
            withinVariances.push(ss / nums.length);
            areaMeans.push(m);
        }
        else if (vals.length >= 1 && vals.every((v) => typeof v === 'number')) {
            areaMeans.push(vals[0]);
        }
    }
    return {
        constantWithinAreas: constant,
        meanWithinVariance: withinVariances.length > 0 ? meanOf(withinVariances) : 0,
        betweenAreaVariance: areaMeans.length >= 2 ? sdOf(areaMeans) ** 2 : 0,
    };
}
/** All distinct non-missing values of a profiled column, ascending. */
export function distinctValuesOf(table, column) {
    const idx = columnIndex(table, column);
    if (idx < 0)
        return [];
    const set = new Set();
    for (const row of table.rows) {
        const v = row[idx] ?? null;
        if (!isMissingCell(v))
            set.add(v);
    }
    return [...set].sort(compareValues);
}
/** Frequency table for one column (all values, profile ordering). */
export function valueCountsOf(table, column) {
    const idx = columnIndex(table, column);
    if (idx < 0)
        return [];
    const col = table.columns[idx];
    const labels = new Map();
    for (const vl of col.valueLabels)
        labels.set(vl.value, vl.label);
    return sortedValueCounts(scanColumn(table, idx), labels);
}
/** Label attached to a value, when the column defines one. */
export function labelOf(table, column, value) {
    const idx = columnIndex(table, column);
    if (idx < 0)
        return undefined;
    return table.columns[idx].valueLabels.find((vl) => vl.value === value)
        ?.label;
}
//# sourceMappingURL=profile.js.map